import React, { useMemo, useState } from 'react'
import {
  Alert,
  Box,
  Button,
  Chip,
  Collapse,
  FormControl,
  InputLabel,
  Link,
  MenuItem,
  Select,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material'
import BlockOutlinedIcon from '@mui/icons-material/BlockOutlined'
import AccessTimeOutlinedIcon from '@mui/icons-material/AccessTimeOutlined'
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'
import LightbulbOutlinedIcon from '@mui/icons-material/LightbulbOutlined'
import HelpOutlineIcon from '@mui/icons-material/HelpOutline'
import NotesOutlinedIcon from '@mui/icons-material/NotesOutlined'
import AssignmentOutlinedIcon from '@mui/icons-material/AssignmentOutlined'
import { useLocation, useParams } from 'react-router-dom'

import { useAuth } from '../../contexts/AuthContext'
import { useAppInfo } from '../../hooks/useVersion'
import { feedbackApi } from '../../services/feedbackApi'
import { poolwerxColors } from '../../theme/colors'
import { COMPACT_TEXTFIELD_SX } from '../../theme/textFieldStyles'
import {
  AppDrawerBody,
  AppDrawerFooter,
  AppDrawerMetadataRow,
  AppDrawerSection,
} from '../shared/AppDrawer'
import {
  FEEDBACK_IMPACT_OPTIONS,
  FEEDBACK_TYPE_OPTIONS,
  type FeedbackImpact,
  type FeedbackType,
} from '../../types/feedback'
import { buildFeedbackContext, isMeaningfulSha } from '../../utils/feedbackContext'
import { formatDate } from '../../utils/inspectorFormatters'

interface FeedbackFormProps {
  presetType?: FeedbackType
  onSubmitted: () => void
  onCancel: () => void
  franchiseData?: { fp_code: string; franchise_name: string; country: string } | null
}

const DETAILS_MIN = 10
const DETAILS_MAX = 4000
const TRYING_MAX = 1000

const IMPACT_ICONS: Record<FeedbackImpact, React.ReactElement> = {
  blocking: <BlockOutlinedIcon sx={{ fontSize: 14 }} />,
  slowing: <AccessTimeOutlinedIcon sx={{ fontSize: 14 }} />,
  minor: <InfoOutlinedIcon sx={{ fontSize: 14 }} />,
  idea: <LightbulbOutlinedIcon sx={{ fontSize: 14 }} />,
}

const IMPACT_SHORT_LABEL: Partial<Record<FeedbackImpact, string>> = {
  slowing: 'Slowing',
}

// "Idea" is a type-like concept (new_feature), not a true impact. We
// never offer it in the impact toggle; for new_feature submissions we
// store impact='idea' implicitly so the backend record still reflects
// it without forcing the user to pick a misleading severity.
const IMPACT_TOGGLE_OPTIONS: FeedbackImpact[] = ['blocking', 'slowing', 'minor']
const IDEA_LIKE_TYPES: FeedbackType[] = ['new_feature']


const FeedbackForm: React.FC<FeedbackFormProps> = ({
  presetType,
  onSubmitted,
  onCancel,
  franchiseData,
}) => {
  const { user } = useAuth()
  const { version: appVersion } = useAppInfo()
  const location = useLocation()
  const params = useParams() as Record<string, string>

  const [type, setType] = useState<FeedbackType>(presetType || 'issue')
  const [impact, setImpact] = useState<FeedbackImpact>('slowing')
  const isIdeaType = IDEA_LIKE_TYPES.includes(type)
  const [details, setDetails] = useState('')
  const [tryingToDo, setTryingToDo] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [showFullContext, setShowFullContext] = useState(false)

  const context = useMemo(
    () =>
      buildFeedbackContext({
        user,
        franchise: franchiseData ?? null,
        location: { pathname: location.pathname, search: location.search },
        params,
        appVersion,
      }),
    [user, franchiseData, location.pathname, location.search, params, appVersion],
  )

  const detailsValid = details.trim().length >= DETAILS_MIN
  const detailsTooLong = details.length > DETAILS_MAX
  const tryingTooLong = tryingToDo.length > TRYING_MAX

  const canSubmit = !submitting && detailsValid && !detailsTooLong && !tryingTooLong

  const origin =
    typeof window !== 'undefined' && window.location?.origin ? window.location.origin : ''
  const fullPageUrl = `${origin}${location.pathname}${location.search || ''}`

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault()
    if (!canSubmit) return
    setSubmitting(true)
    setError(null)
    try {
      await feedbackApi.submit({
        type,
        impact: isIdeaType ? 'idea' : impact,
        title: null,
        details: details.trim(),
        trying_to_do: tryingToDo.trim() || null,
        follow_up_ok: false,
        context,
      })
      onSubmitted()
    } catch (err: any) {
      const detail = err?.response?.data?.detail
      const msg =
        typeof detail === 'string'
          ? detail
          : err?.response?.status === 429
            ? 'You have submitted a lot of feedback recently. Please try again later.'
            : 'Something went wrong sending your feedback. Please try again.'
      setError(msg)
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <Box
      component="form"
      onSubmit={handleSubmit}
      sx={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 }}
    >
      <AppDrawerBody padding={{ px: 1.5, pt: 1.5, pb: 1.5 }}>
        <Stack spacing={1.25}>
          <AppDrawerSection
            icon={<HelpOutlineIcon sx={{ fontSize: 18 }} />}
            title="About"
          >
            <Stack spacing={1}>
              <FormControl fullWidth size="small">
                <InputLabel id="feedback-type-label" sx={{ fontSize: '0.85rem' }}>
                  Type
                </InputLabel>
                <Select
                  labelId="feedback-type-label"
                  label="Type"
                  value={type}
                  onChange={e => setType(e.target.value as FeedbackType)}
                  sx={{
                    fontSize: '0.85rem',
                    '& .MuiOutlinedInput-input': { py: 0.75 },
                  }}
                >
                  {FEEDBACK_TYPE_OPTIONS.map(opt => (
                    <MenuItem key={opt.value} value={opt.value} sx={{ fontSize: '0.85rem' }}>
                      {opt.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <Box>
                <Typography
                  component="div"
                  variant="caption"
                  color="text.secondary"
                  sx={{
                    fontWeight: 600,
                    fontSize: '0.65rem',
                    letterSpacing: 0.4,
                    textTransform: 'uppercase',
                    mb: 0.5,
                  }}
                >
                  Impact
                </Typography>
                {isIdeaType ? (
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ display: 'block', fontSize: '0.72rem', lineHeight: 1.5 }}
                  >
                    Ideas are sent as suggestions, not issue reports.
                  </Typography>
                ) : (
                  <ToggleButtonGroup
                    value={impact}
                    exclusive
                    size="small"
                    onChange={(_e, v: FeedbackImpact | null) => {
                      if (v) setImpact(v)
                    }}
                    sx={{
                      gap: 0.5,
                      '& .MuiToggleButton-root': {
                        textTransform: 'none',
                        fontWeight: 500,
                        fontSize: '0.72rem',
                        px: 1.25,
                        py: 0.375,
                        gap: 0.5,
                        borderRadius: 1,
                        border: '1px solid',
                        borderColor: 'divider',
                        color: 'text.secondary',
                        lineHeight: 1.4,
                        whiteSpace: 'nowrap',
                        // ToggleButtonGroup strips the border between
                        // adjacent buttons by default - we want each
                        // pill outlined, so put it back.
                        '&:not(:first-of-type)': {
                          borderLeft: '1px solid',
                          borderLeftColor: 'divider',
                          ml: 0,
                        },
                      },
                      '& .MuiToggleButton-root.Mui-selected': {
                        bgcolor: 'rgba(0, 127, 152, 0.08)',
                        color: poolwerxColors.teal,
                        borderColor: 'rgba(0, 127, 152, 0.45)',
                        '&:hover': { bgcolor: 'rgba(0, 127, 152, 0.12)' },
                        '&:not(:first-of-type)': {
                          borderLeftColor: 'rgba(0, 127, 152, 0.45)',
                        },
                      },
                      '& .MuiToggleButton-root[value="blocking"].Mui-selected': {
                        bgcolor: poolwerxColors.redAccent,
                        color: '#fff',
                        borderColor: poolwerxColors.redAccent,
                        '&:hover': { bgcolor: poolwerxColors.redAccent },
                      },
                    }}
                    aria-label="Impact"
                  >
                    {IMPACT_TOGGLE_OPTIONS.map(value => {
                      const opt = FEEDBACK_IMPACT_OPTIONS.find(o => o.value === value)!
                      return (
                        <ToggleButton key={value} value={value} aria-label={opt.label}>
                          {IMPACT_ICONS[value]}
                          {IMPACT_SHORT_LABEL[value] ?? opt.label}
                        </ToggleButton>
                      )
                    })}
                  </ToggleButtonGroup>
                )}
              </Box>
            </Stack>
          </AppDrawerSection>

          <AppDrawerSection
            icon={<NotesOutlinedIcon sx={{ fontSize: 18 }} />}
            title="Details"
          >
            <Stack spacing={1}>
              <TextField
                label="What's happening?"
                value={details}
                onChange={e => setDetails(e.target.value)}
                size="small"
                fullWidth
                required
                multiline
                minRows={3}
                maxRows={10}
                inputProps={{ maxLength: DETAILS_MAX + 200 }}
                helperText={
                  detailsTooLong
                    ? `Too long - ${details.length}/${DETAILS_MAX}`
                    : !detailsValid && details.length > 0
                      ? `At least ${DETAILS_MIN} characters`
                      : `${details.length}/${DETAILS_MAX}`
                }
                error={detailsTooLong || (details.length > 0 && !detailsValid)}
                sx={COMPACT_TEXTFIELD_SX}
              />
              <TextField
                label="What were you trying to do? (optional)"
                value={tryingToDo}
                onChange={e => setTryingToDo(e.target.value)}
                size="small"
                fullWidth
                multiline
                minRows={1}
                maxRows={5}
                inputProps={{ maxLength: TRYING_MAX + 100 }}
                helperText={
                  tryingTooLong ? `Too long - ${tryingToDo.length}/${TRYING_MAX}` : undefined
                }
                error={tryingTooLong}
                sx={COMPACT_TEXTFIELD_SX}
              />
            </Stack>
          </AppDrawerSection>

          <AppDrawerSection
            icon={<AssignmentOutlinedIcon sx={{ fontSize: 18 }} />}
            title="We'll include with your report"
          >
            <Stack spacing={0.5}>
              <AppDrawerMetadataRow label="Page" value={fullPageUrl} mono />
              {context.fp_code && (
                <AppDrawerMetadataRow
                  label="Franchise"
                  value={`${context.fp_code}${context.fp_name ? ' · ' + context.fp_name : ''}${context.country ? ' (' + context.country + ')' : ''}`}
                />
              )}
              <AppDrawerMetadataRow
                label="Browser"
                value={`${context.browser.family} on ${context.browser.os}`}
              />
              {context.timezone && (
                <AppDrawerMetadataRow label="Timezone" value={context.timezone} />
              )}
            </Stack>
            <Box sx={{ mt: 1 }}>
              <Link
                component="button"
                type="button"
                variant="caption"
                underline="hover"
                onClick={() => setShowFullContext(v => !v)}
                sx={{ fontSize: '0.72rem' }}
              >
                {showFullContext ? 'Hide full context' : 'View full context'}
              </Link>
              <Collapse in={showFullContext} unmountOnExit>
                <Stack spacing={0.5} sx={{ mt: 0.75 }}>
                  {context.browser.viewport_w > 0 && context.browser.viewport_h > 0 && (
                    <AppDrawerMetadataRow
                      label="Viewport"
                      value={`${context.browser.viewport_w} × ${context.browser.viewport_h}`}
                    />
                  )}
                  {context.browser.language && (
                    <AppDrawerMetadataRow label="Language" value={context.browser.language} />
                  )}
                  {context.app?.version && (
                    <AppDrawerMetadataRow
                      label="App"
                      value={`${context.app.version} (${context.app.env})`}
                    />
                  )}
                  {isMeaningfulSha(context.app?.sha) && (
                    <AppDrawerMetadataRow
                      label="Build"
                      value={context.app!.sha.slice(0, 7)}
                      mono
                    />
                  )}
                  {context.timestamp && (
                    <AppDrawerMetadataRow
                      label="Timestamp"
                      value={formatDate(context.timestamp, 'datetime', {
                        includeTimezone: false,
                      })}
                    />
                  )}
                  {context.correlation_id && (
                    <AppDrawerMetadataRow
                      label="Correlation"
                      value={context.correlation_id}
                      mono
                    />
                  )}
                  {context.route.referrer && (
                    <AppDrawerMetadataRow
                      label="Came from"
                      value={context.route.referrer}
                      mono
                    />
                  )}
                  {Object.entries(context.route.params || {}).map(([k, v]) => (
                    <AppDrawerMetadataRow
                      key={`p-${k}`}
                      label={k}
                      value={String(v)}
                      mono
                    />
                  ))}
                  {Object.keys(context.route.filters || {}).length > 0 && (
                    <AppDrawerMetadataRow
                      label="Filters"
                      value={
                        <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                          {Object.entries(context.route.filters).map(([k, v]) => (
                            <Chip
                              key={`f-${k}`}
                              size="small"
                              label={`${k}: ${v}`}
                              variant="outlined"
                              sx={{
                                height: 18,
                                fontSize: '0.65rem',
                                fontWeight: 500,
                                '& .MuiChip-label': { px: 0.75 },
                              }}
                            />
                          ))}
                        </Stack>
                      }
                    />
                  )}
                </Stack>
              </Collapse>
            </Box>
          </AppDrawerSection>

          {error && <Alert severity="error">{error}</Alert>}
        </Stack>
      </AppDrawerBody>

      <AppDrawerFooter padding={{ px: 2, pt: 1, pb: 1 }}>
        <Stack direction="row" justifyContent="flex-end" spacing={1}>
          <Button size="small" onClick={onCancel} disabled={submitting}>
            Cancel
          </Button>
          <Button type="submit" variant="contained" size="small" disabled={!canSubmit}>
            {submitting ? 'Sending...' : 'Send'}
          </Button>
        </Stack>
      </AppDrawerFooter>
    </Box>
  )
}

export default FeedbackForm
